/**
 * Pixel-buffer primitives for the document pre-processing pipeline.
 *
 * Everything here is framework-agnostic: an {@link ImagePixels} is just RGBA
 * bytes + width/height (structurally compatible with the DOM `ImageData`), so
 * the same code runs in the browser and in a Node Lambda. No canvas, no OpenCV.
 *
 * NOTE on colour order: the Python source uses OpenCV BGR throughout. Here we
 * keep images in **RGBA** (canvas-native) and convert to BGR only where a model
 * expects it (YuNet). All thresholds are channel-symmetric or applied after an
 * explicit RGB→HSV/gray conversion, so the RGB-vs-BGR difference is immaterial
 * except where noted.
 */
import { ImagePixels } from '../../core/ports';

/** A binary/scalar single-channel mask, parallel to {@link ImagePixels}. */
export interface Mask {
  data: Uint8Array;
  width: number;
  height: number;
}

export function createImage(width: number, height: number): ImagePixels {
  return { data: new Uint8ClampedArray(width * height * 4), width, height };
}

export function createMask(width: number, height: number, fill = 0): Mask {
  const data = new Uint8Array(width * height);
  if (fill) data.fill(fill);
  return { data, width, height };
}

export function cloneImage(img: ImagePixels): ImagePixels {
  return { data: new Uint8ClampedArray(img.data), width: img.width, height: img.height };
}

/** White-fill a fresh RGBA image (matches OpenCV borderValue=(255,255,255)). */
export function whiteImage(width: number, height: number): ImagePixels {
  const img = createImage(width, height);
  img.data.fill(255);
  return img;
}

/**
 * Nearest/bilinear resize of an RGBA image. Bilinear matches `cv2.resize`'s
 * default (INTER_LINEAR) closely enough for our thresholding paths.
 */
export function resizeImage(img: ImagePixels, dstW: number, dstH: number): ImagePixels {
  dstW = Math.max(1, Math.round(dstW));
  dstH = Math.max(1, Math.round(dstH));
  const out = createImage(dstW, dstH);
  const { data: src, width: sw, height: sh } = img;
  const sx = sw / dstW;
  const sy = sh / dstH;
  for (let y = 0; y < dstH; y++) {
    const fy = (y + 0.5) * sy - 0.5;
    const y0 = Math.max(0, Math.floor(fy));
    const y1 = Math.min(sh - 1, y0 + 1);
    const wy = fy - y0;
    for (let x = 0; x < dstW; x++) {
      const fx = (x + 0.5) * sx - 0.5;
      const x0 = Math.max(0, Math.floor(fx));
      const x1 = Math.min(sw - 1, x0 + 1);
      const wx = fx - x0;
      const i00 = (y0 * sw + x0) * 4;
      const i01 = (y0 * sw + x1) * 4;
      const i10 = (y1 * sw + x0) * 4;
      const i11 = (y1 * sw + x1) * 4;
      const o = (y * dstW + x) * 4;
      for (let c = 0; c < 4; c++) {
        const top = src[i00 + c] * (1 - wx) + src[i01 + c] * wx;
        const bot = src[i10 + c] * (1 - wx) + src[i11 + c] * wx;
        out.data[o + c] = top * (1 - wy) + bot * wy;
      }
    }
  }
  return out;
}

/** Crop an inclusive bounding box [x0,x1] x [y0,y1]. */
export function cropImage(img: ImagePixels, x0: number, y0: number, x1: number, y1: number): ImagePixels {
  x0 = Math.max(0, Math.min(img.width - 1, x0 | 0));
  x1 = Math.max(0, Math.min(img.width - 1, x1 | 0));
  y0 = Math.max(0, Math.min(img.height - 1, y0 | 0));
  y1 = Math.max(0, Math.min(img.height - 1, y1 | 0));
  const w = x1 - x0 + 1;
  const h = y1 - y0 + 1;
  const out = createImage(w, h);
  for (let y = 0; y < h; y++) {
    const srcRow = ((y + y0) * img.width + x0) * 4;
    const dstRow = y * w * 4;
    out.data.set(img.data.subarray(srcRow, srcRow + w * 4), dstRow);
  }
  return out;
}

/** Grayscale plane (ITU-R 601, matching cv2.cvtColor BGR2GRAY weights). */
export function toGray(img: ImagePixels): Float32Array {
  const { data, width, height } = img;
  const g = new Float32Array(width * height);
  for (let i = 0, p = 0; i < g.length; i++, p += 4) {
    // OpenCV: 0.299*R + 0.587*G + 0.114*B (same regardless of RGB/BGR storage
    // because we read R,G,B explicitly).
    g[i] = 0.299 * data[p] + 0.587 * data[p + 1] + 0.114 * data[p + 2];
  }
  return g;
}

/** Per-pixel RGB→HSV in OpenCV's ranges: H 0..179, S 0..255, V 0..255. */
export function toHsv(img: ImagePixels): { h: Uint8Array; s: Uint8Array; v: Uint8Array } {
  const n = img.width * img.height;
  const h = new Uint8Array(n);
  const s = new Uint8Array(n);
  const v = new Uint8Array(n);
  const d = img.data;
  for (let i = 0, p = 0; i < n; i++, p += 4) {
    const r = d[p], g = d[p + 1], b = d[p + 2];
    const mx = Math.max(r, g, b);
    const mn = Math.min(r, g, b);
    const delta = mx - mn;
    v[i] = mx;
    s[i] = mx === 0 ? 0 : Math.round((delta * 255) / mx);
    let hue = 0;
    if (delta !== 0) {
      if (mx === r) hue = 60 * (((g - b) / delta) % 6);
      else if (mx === g) hue = 60 * ((b - r) / delta + 2);
      else hue = 60 * ((r - g) / delta + 4);
      if (hue < 0) hue += 360;
    }
    h[i] = Math.round(hue / 2); // OpenCV packs hue into 0..179
  }
  return { h, s, v };
}

/** Rotate an RGBA image by an exact multiple of 90° clockwise (no resampling). */
export function rotate90(img: ImagePixels, kCw: 0 | 90 | 180 | 270): ImagePixels {
  if (kCw === 0) return cloneImage(img);
  const { data, width: W, height: H } = img;
  if (kCw === 180) {
    const out = createImage(W, H);
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        const si = (y * W + x) * 4;
        const di = ((H - 1 - y) * W + (W - 1 - x)) * 4;
        out.data[di] = data[si];
        out.data[di + 1] = data[si + 1];
        out.data[di + 2] = data[si + 2];
        out.data[di + 3] = data[si + 3];
      }
    }
    return out;
  }
  const out = createImage(H, W); // dimensions swap
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const si = (y * W + x) * 4;
      let dx: number, dy: number;
      if (kCw === 90) {
        dx = H - 1 - y;
        dy = x;
      } else {
        // 270
        dx = y;
        dy = W - 1 - x;
      }
      const di = (dy * H + dx) * 4;
      out.data[di] = data[si];
      out.data[di + 1] = data[si + 1];
      out.data[di + 2] = data[si + 2];
      out.data[di + 3] = data[si + 3];
    }
  }
  return out;
}
